import { Alert, Modal, message, Select, Spin, Tag } from 'antd';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../config/api';
import { useAuthStore } from '../store/authStore';

interface Candidate {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  Organization?: { id: string; name: string } | null;
}

interface MasqueradeStartModalProps {
  open: boolean;
  onClose: () => void;
}

/** A két megkülönböztetett hibaeset i18n-kulcsa. */
type ListError = 'masquerade.list_forbidden' | 'masquerade.list_failed';

const fullName = (c: Candidate): string => `${c.firstName} ${c.lastName}`.trim() || c.email;

/**
 * Az imitálható felhasználók külön végpontról jönnek (`/masquerade/users`),
 * ugyanazzal a SUPER_ADMIN kapuval, mint maga az indítás — a lista nem
 * szivároghat ki egy gyengébben védett útvonalon.
 */
const MasqueradeStartModal: React.FC<MasqueradeStartModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { startMasquerade } = useAuthStore();
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [selected, setSelected] = useState<string | undefined>();
  // A hiba külön állapot: enélkül egy 403 és egy üres találati lista ugyanazt
  // az „nincs kit imitálni" képernyőt adta, tehát egy elromlott jogosultság-kapu
  // úgy nézett ki, mintha nem lenne felhasználó.
  const [error, setError] = useState<ListError | null>(null);

  // A szűrés a szerveren fut és korlátos találatot ad, ezért gépelésre újra
  // kell kérdezni. A `seq` dobja el a késve beérkező, elavult válaszokat.
  const seq = useRef(0);

  const load = useCallback((q?: string) => {
    const mine = ++seq.current;
    setLoading(true);
    setError(null);
    api
      .get<Candidate[]>('/masquerade/users', { params: q ? { q } : undefined })
      .then((res) => {
        if (seq.current === mine) setCandidates(res.data);
      })
      .catch((err: { response?: { status?: number } }) => {
        if (seq.current !== mine) return;
        setCandidates([]);
        setError(
          err?.response?.status === 403 ? 'masquerade.list_forbidden' : 'masquerade.list_failed'
        );
      })
      .finally(() => {
        if (seq.current === mine) setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!open) {
      setSelected(undefined);
      setError(null);
      return;
    }
    load();
  }, [open, load]);

  // Gépelés közbeni újralekérdezés, hogy a plafon fölötti találatok is
  // elérhetők legyenek — ne csak az első 50-en belül lehessen keresni.
  const debounce = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const handleSearch = useCallback(
    (value: string) => {
      if (debounce.current) clearTimeout(debounce.current);
      const q = value.trim();
      debounce.current = setTimeout(() => load(q || undefined), 250);
    },
    [load]
  );

  // A modál bezárása ne hagyjon futó időzítőt maga után.
  useEffect(
    () => () => {
      if (debounce.current) clearTimeout(debounce.current);
    },
    []
  );

  const handleStart = async () => {
    if (!selected) return;
    const target = candidates.find((c) => c.id === selected);
    setStarting(true);
    try {
      await startMasquerade(selected);
      message.success(t('masquerade.started', { name: target ? fullName(target) : '' }));
      onClose();
      // A menü, a jogosultságok és minden betöltött lista az imitált
      // szemszögéből érvényes — a főoldalról indulva nem marad a képernyőn
      // olyan adat, amit az imitált felhasználó nem láthatna.
      navigate('/');
    } catch {
      message.error(t('masquerade.failed'));
    } finally {
      setStarting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={t('masquerade.modal_title')}
      onCancel={onClose}
      onOk={handleStart}
      okText={t('masquerade.start')}
      cancelText={t('common.cancel')}
      okButtonProps={{ disabled: !selected, loading: starting }}
      destroyOnHidden
    >
      <Alert type="warning" showIcon message={t('masquerade.hint')} style={{ marginBottom: 16 }} />
      {error && <Alert type="error" showIcon message={t(error)} style={{ marginBottom: 16 }} />}
      <Select
        showSearch
        style={{ width: '100%' }}
        value={selected}
        onChange={setSelected}
        onSearch={handleSearch}
        loading={loading}
        placeholder={t('masquerade.select_placeholder')}
        notFoundContent={loading ? <Spin size="small" /> : t('masquerade.no_candidates')}
        // A szűrés a szerveren fut; a kliensoldali szűrő elrejtené a friss
        // találatokat, amíg a keresőmező tartalma nem illik a címkére.
        filterOption={false}
        options={candidates.map((c) => ({
          value: c.id,
          label: `${fullName(c)} · ${c.email}`,
          candidate: c,
        }))}
        optionRender={({ data }) => {
          const c = (data as { candidate: Candidate }).candidate;
          return (
            <span>
              {fullName(c)} <Tag>{c.role}</Tag>
              {c.Organization ? <Tag color="blue">{c.Organization.name}</Tag> : null}
              <br />
              <small>{c.email}</small>
            </span>
          );
        }}
      />
    </Modal>
  );
};

export default MasqueradeStartModal;
